import classNames from 'classnames';
import React, { useLayoutEffect, useRef, useState } from 'react';
import { useEffectEvent } from 'use-effect-event';
import * as styles from './MagneticWallItem.module.scss';
import Button from '../components/Button';
import EmojiPicker, { EmojiPickerPopOver } from '../components/EmojiPicker';
import Tooltip from '../components/Tooltip';
import type { CompactPopOverSide } from './comment/getMagneticWallItemLayout';
import showFullPageSheet from './comment/showFullPageSheet';
import commentEmojiIcon from '../icons/non-prefilled/16x16/commentEmoji.svg';
import addTestId from '../utils/addTestId';
import ClickOutsideHandler from '../utils/ClickOutsideHandler';
import { useIsSize } from '../utils/responsive/isSize';
import Sizes from '../utils/responsive/Sizes';

interface ResponsivePopoverProps {
  target: React.ReactNode;
  content: React.ReactNode;
  open: boolean;
  onClose(): void;
  isPopOverMinWidthDisabled?: boolean;
  compactPopOverSide: CompactPopOverSide;
  onPopOverHeightChange: (height: number) => void;
}

const ResponsivePopover = ({
  target,
  content,
  open,
  onClose,
  isPopOverMinWidthDisabled,
  compactPopOverSide,
  onPopOverHeightChange,
}: ResponsivePopoverProps) => {
  const popOverRef = useRef<HTMLDivElement | null>(null);

  const onCompactPopOverHeightChangeEvent = useEffectEvent(
    onPopOverHeightChange,
  );

  const isShowingPopOver = open;

  useLayoutEffect(() => {
    const popOver = popOverRef.current;
    if (!isShowingPopOver || !popOver) return undefined;

    const getHeight = () => popOver.getBoundingClientRect().height;
    const initialHeight = getHeight();

    onCompactPopOverHeightChangeEvent(initialHeight);
    let lastHeight = initialHeight;

    const observer = new ResizeObserver(() => {
      const height = getHeight();
      if (height !== lastHeight) {
        lastHeight = height;
        onCompactPopOverHeightChangeEvent(height);
      }
    });

    observer.observe(popOver);
    return () => {
      observer.disconnect();
    };
  }, [isShowingPopOver]);

  if (!open) {
    return <>{target}</>;
  }

  if (showFullPageSheet()) return null;

  return (
    <>
      <ClickOutsideHandler
        onClickOutside={onClose}
        className={classNames(styles.popOverContainer, {
          [styles.minWidthDisabled]: isPopOverMinWidthDisabled,
          [styles.sideTop]: compactPopOverSide === 'top',
        })}
      >
        <div ref={popOverRef} {...addTestId('magnetic-wall-item-pop-over')}>
          {content}
        </div>
      </ClickOutsideHandler>
      {target}
    </>
  );
};

export type MagneticWallItemSize = 'full' | 'compact';

interface RootProps {
  fullView?: React.ReactNode;
  compactView: React.ReactNode;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  onMouseEnter?: React.MouseEventHandler<HTMLDivElement>;
  onMouseLeave?: React.MouseEventHandler<HTMLDivElement>;
  onMouseDown?: React.MouseEventHandler<HTMLDivElement>;
  onTouchStart?: React.TouchEventHandler<HTMLDivElement>;
  id: string;
  focused: boolean;
  wide?: boolean;
  size: MagneticWallItemSize;
}

const Root = React.forwardRef<HTMLDivElement, RootProps>(
  (
    {
      fullView,
      compactView,
      id,
      focused,
      wide,
      onClick,
      onMouseEnter,
      onMouseLeave,
      onTouchStart,
      onMouseDown,
      size,
    },
    ref,
  ) => {
    return (
      <div
        ref={ref}
        {...addTestId(`magnetic-wall-item-${id}`)}
        className={styles.item}
      >
        {size === 'full' && !!fullView && (
          <div
            className={classNames(styles.fullContainer, {
              [styles.focused]: focused,
              [styles.wideContainer]: wide,
            })}
            onClick={onClick}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onMouseDown={onMouseDown}
            onTouchStart={onTouchStart}
          >
            {fullView}
          </div>
        )}
        {size === 'compact' && (
          <div
            className={classNames(styles.compactContainer, {
              [styles.focused]: focused,
            })}
            onClick={onClick}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onMouseDown={onMouseDown}
            onTouchStart={onTouchStart}
            {...addTestId('magnetic-wall-item-compact-container')}
          >
            {compactView}
          </div>
        )}
      </div>
    );
  },
);

interface CardProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'className'> {
  overlapped: boolean;
  focused: boolean;
  focusable?: boolean;
}

const Card = ({
  children,
  overlapped,
  focused,
  focusable,
  ...rest
}: CardProps) => {
  return (
    <div
      {...rest}
      className={classNames(styles.card, {
        [styles.focused]: focused,
        [styles.focusable]: focusable,
        [styles.overlapped]: overlapped,
      })}
    >
      {children}
    </div>
  );
};

interface WithAvatarProps {
  children: React.ReactNode;
  avatar: React.ReactNode;
  disabled?: boolean;
  innerTestId?: string;
}

const WithAvatar = ({
  children,
  avatar,
  disabled,
  innerTestId,
}: WithAvatarProps) => (
  <div
    className={classNames(styles.avatarContainer, {
      [styles.avatarContainerDisabled]: disabled,
    })}
  >
    <div className={styles.avatar}>{avatar}</div>
    <div className={styles.inner} {...addTestId(innerTestId)}>
      {children}
    </div>
  </div>
);

interface ActionContainerProps {
  children: React.ReactNode;
}

const ActionContainer = ({ children }: ActionContainerProps) => (
  <div className={styles.actionContainer}>{children}</div>
);

interface IconButtonProps {
  icon: React.ReactNode;
  onClick(): void;
  tooltip: string;
  tooltipPlacement?: React.ComponentProps<typeof Tooltip>['placement'];
  active?: boolean;
  testId?: string;
  disabled?: boolean;
}

const IconButton = ({
  icon,
  onClick,
  tooltip,
  tooltipPlacement = 'bottom',
  active,
  testId,
  disabled,
}: IconButtonProps) => (
  <Tooltip
    placement={tooltipPlacement}
    content={disabled ? undefined : tooltip}
  >
    <Button
      active={active}
      className={classNames(styles.iconButton, { [styles.active]: active })}
      icon={icon}
      onClick={onClick}
      testId={testId}
      aria-label={tooltip}
      disabled={disabled}
    />
  </Tooltip>
);

interface SubmitButtonProps {
  onClick(): void;
  disabled: boolean;
  children: React.ReactNode;
}

const SubmitButton = ({ disabled, onClick, children }: SubmitButtonProps) => (
  <Button
    disabled={disabled}
    type="button"
    variant="primary"
    size="small"
    onClick={onClick}
  >
    {children}
  </Button>
);

interface SecondaryButtonProps {
  onClick(): void;
  disabled?: boolean;
  children: React.ReactNode;
}

const SecondaryButton = ({
  disabled,
  onClick,
  children,
}: SecondaryButtonProps) => (
  <Button
    disabled={disabled}
    type="button"
    variant="secondary"
    size="small"
    onClick={onClick}
  >
    {children}
  </Button>
);

interface ButtonGroupProps {
  children: React.ReactNode;
}

const ButtonGroup = ({ children }: ButtonGroupProps) => (
  <div className={styles.buttonGroup}>{children}</div>
);

interface EmojiPickerButtonProps {
  onPick: (emoji: string) => void;
  tooltipPlacement?: React.ComponentProps<typeof Tooltip>['placement'];
  disabled?: boolean;
}

const EmojiPickerButton = ({
  onPick,
  tooltipPlacement = 'bottom',
  disabled,
}: EmojiPickerButtonProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const isSmall = useIsSize(Sizes.SM);

  const handleSetIsOpen = (open: boolean) => {
    setIsOpen(!disabled && open);
  };

  return (
    <EmojiPickerPopOver
      open={isOpen}
      bounds={{ left: -6 }}
      placement={isSmall ? 'bottom-start' : 'left-start'}
      setOpen={handleSetIsOpen}
      target={
        <IconButton
          tooltip="Insert emoji"
          tooltipPlacement={tooltipPlacement}
          icon={commentEmojiIcon}
          onClick={() => handleSetIsOpen(true)}
          active={isOpen}
          disabled={disabled}
        />
      }
    >
      <EmojiPicker
        onPick={(emoji) => {
          handleSetIsOpen(false);
          onPick(emoji);
        }}
      />
    </EmojiPickerPopOver>
  );
};

export default {
  Root,
  EmojiPickerButton,
  ButtonGroup,
  SecondaryButton,
  SubmitButton,
  IconButton,
  ActionContainer,
  WithAvatar,
  Card,
  ResponsivePopover,
};
